import React, { useRef, useCallback } from 'react';

const CODE_LENGTH = 8;

export type VerificationCodeInputProps = {
  /**
   * The current code value as a string
   */
  value: string;
  /**
   * Callback when the code value changes
   */
  onChange: (value: string) => void;
};

/**
 * An 8-digit OTP-style input where each character gets its own box.
 */
export const VerificationCodeInput = ({ value, onChange }: VerificationCodeInputProps) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const focusInput = useCallback((index: number) => {
    if (index >= 0 && index < CODE_LENGTH) {
      inputRefs.current[index]?.focus();
    }
  }, []);

  const updateValue = useCallback(
    (index: number, char: string) => {
      const chars = value.padEnd(CODE_LENGTH, ' ').split('');
      chars[index] = char;
      onChange(chars.join('').trim());
    },
    [value, onChange],
  );

  const handleChange = useCallback(
    (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (!raw) return;

      // Take the last character (handles both fresh input and typing over existing)
      const char = raw.slice(-1);
      updateValue(index, char);
      focusInput(index + 1);
    },
    [focusInput, updateValue],
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        e.preventDefault();
        if (value[index]) {
          updateValue(index, '');
        } else if (index > 0) {
          updateValue(index - 1, '');
          focusInput(index - 1);
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        focusInput(index - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        focusInput(index + 1);
      }
    },
    [value, focusInput, updateValue],
  );

  const handlePaste = useCallback(
    (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData
        .getData('text')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, CODE_LENGTH - index);
      const chars = value.padEnd(CODE_LENGTH, ' ').split('');
      for (let i = 0; i < pasted.length; i++) {
        chars[index + i] = pasted[i];
      }
      onChange(chars.join('').trim());
      focusInput(Math.min(index + pasted.length, CODE_LENGTH - 1));
    },
    [value, onChange, focusInput],
  );

  return (
    <div className="flex justify-center gap-2">
      {Array.from({ length: CODE_LENGTH }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="text"
          maxLength={CODE_LENGTH}
          value={value[index]?.toUpperCase() || ''}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          onFocus={(e) => e.target.select()}
          aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
          className="size-[44px] rounded-medium border border-solid border-grayscale-400 bg-white text-center text-sm uppercase focus:border-purple-700 focus:outline-none"
        />
      ))}
    </div>
  );
};

export default VerificationCodeInput;
